import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    issue_type: '',
    subject: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const messageRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const { error } = await supabase
        .from('support_tickets')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          issue_type: formData.issue_type,
          subject: formData.subject,
          description: formData.description,
          status: 'open',
          priority: 'normal',
          source: 'rmq_digital',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setSubmitMessage('Your support request has been submitted successfully. We will respond within 24-48 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        issue_type: '',
        subject: '',
        description: ''
      });
      
      // Scroll to success message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      setSubmitMessage('There was an error submitting your support request. Please email support@rmqdigital.com or try again.');
      console.error('Supabase Error:', error);
      
      // Scroll to error message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{paddingTop: '100px'}}>
      <Helmet>
        <title>Customer Support - Help & Contact | RMQ Digital</title>
        <meta name="description" content="Get help with digital marketing services, SMS campaigns, or email marketing. Email: support@rmqdigital.com. Response within 24-48 hours." />
        <meta name="keywords" content="customer support, help center, contact support, SMS help, email marketing support" />
      </Helmet>
      
      <section style={{padding: '4rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', color: '#1f2937', fontFamily: 'Space Grotesk'}}>Customer Support</h1>
          
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem', alignItems: 'start'}}>
            <div>
              <h2 style={{color: '#6d28d9', marginBottom: '1.5rem', fontSize: '1.5rem'}}>Get Help</h2>
              <p style={{marginBottom: '2rem', lineHeight: '1.8'}}>
                Our support team is here to help you with any questions about our digital marketing services, 
                SMS campaigns, email marketing, or platform features.
              </p>

              <div style={{background: '#ede9fe', padding: '2rem', borderRadius: '12px', marginBottom: '2rem', border: '2px solid #8b5cf6'}}>
                <h3 style={{color: '#6d28d9', marginBottom: '1rem'}}>Support Hours</h3>
                <p style={{color: '#5b21b6'}}><strong>Business Hours:</strong></p>
                <p style={{color: '#5b21b6'}}>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                <p style={{color: '#5b21b6'}}>Saturday - Sunday: Closed</p>
                <p style={{marginTop: '1rem', fontSize: '0.9rem', color: '#5b21b6'}}>
                  We respond to all support requests within 24-48 business hours.
                </p>
              </div>

              <div style={{background: '#f9fafb', padding: '2rem', borderRadius: '12px', marginBottom: '2rem', border: '1px solid #e5e7eb'}}>
                <h3 style={{color: '#1f2937', marginBottom: '1rem'}}>Contact Methods</h3>
                <p><strong>Email:</strong> support@rmqdigital.com</p>
                <p><strong>Sales:</strong> sales@rmqdigital.com</p>
                <p><strong>General:</strong> info@rmqdigital.com</p>
                <p style={{marginTop: '1rem'}}><strong>Company:</strong></p>
                <p><strong>RMQ HOLDINGS INC</strong><br />
                DBA: RMQ Digital<br />
                2022 EAST 9TH STREET<br />
                BROOKLYN, NY 11223</p>
                <p><strong>Owner:</strong> MOSHE QUBRUSI</p>
                <p><strong>EIN:</strong> 99-1828959</p>
              </div>
            </div>

            <div style={{background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'}}>
              <h2 style={{color: '#1f2937', marginBottom: '1.5rem', fontSize: '1.25rem'}}>Submit Support Request</h2>
              
              {submitMessage && (
                <div 
                  ref={messageRef}
                  style={{
                    background: submitMessage.includes('error') ? '#fef2f2' : '#d1fae5',
                    color: submitMessage.includes('error') ? '#991b1b' : '#065f46',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: `2px solid ${submitMessage.includes('error') ? '#ef4444' : '#10b981'}`,
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                >
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Issue Type *
                  </label>
                  <select
                    name="issue_type"
                    value={formData.issue_type}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select issue type</option>
                    <option value="general">General Question</option>
                    <option value="sms">SMS Campaign Issue</option>
                    <option value="email">Email Campaign Issue</option>
                    <option value="billing">Billing Question</option>
                    <option value="technical">Technical Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Brief description of your issue"
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                    Detailed Description *
                  </label>
                  <textarea
                    name="description"
                    rows="6"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Please provide as much detail as possible..."
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '1rem',
                      fontFamily: 'inherit'
                    }}
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Support Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
